import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { CalendarIcon, Crown, PenLine, User as UserIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/avatar";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Switch } from "@/shared/ui/switch";
import { Calendar as CalendarComponent } from "@/shared/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { cn } from "@/shared/lib/utils";
import { Pagination } from "@/shared/widgets/pagination";
import { useTranslation } from "@/shared/i18n";

import { IUser } from "../services/profiles/types";

interface UsersTableViewProps {
  users: IUser[] | undefined;
  isLoading: boolean;
  onVipToggle: ({ id, vip, vipExpiresAt }: { id: string; vip: boolean; vipExpiresAt: string }) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    pageSizeOptions?: number[];
    showPageSizeOptions?: boolean;
    showTotalItems?: boolean;
    syncWithQueryParams?: boolean;
  };
}

const formatDate = (date?: string | null) => {
  if (!date) return "—";
  try {
    return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
};

const getInitials = (email: string, name?: string) => {
  if (name) {
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  }
  return email.slice(0, 2).toUpperCase();
};

export const UsersTableView = ({
  users,
  isLoading,
  onVipToggle,
  pagination,
}: UsersTableViewProps) => {
  const { t } = useTranslation("profiles");
  const navigate = useNavigate();

  const shouldRenderPagination = Boolean(pagination);

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-white/10 bg-[#222222] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/60">{t("user")}</TableHead>
              <TableHead className="text-white/60">{t("email")}</TableHead>
              <TableHead className="text-white/60">{t("subscription")}</TableHead>
              <TableHead className="text-white/60">{t("subscriptionExpiresAt")}</TableHead>
              <TableHead className="text-white/60">{t("vipLabel")}</TableHead>
              <TableHead className="text-white/60">{t("vipExpiresAt")}</TableHead>
              <TableHead className="text-white/60">{t("createdAt")}</TableHead>
              <TableHead className="text-white/60 text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i} className="border-white/10">
                  <TableCell colSpan={8}>
                    <div className="h-8 animate-pulse rounded bg-white/5" />
                  </TableCell>
                </TableRow>
              ))
            ) : users && users.length > 0 ? (
              users.map((user) => {
                const name =
                  user.name || user.username || user.creatorData?.name || user.creatorData?.username || t("user");
                const isVip = Boolean(user.vip);
                const isSubscribed = Boolean(user.subscription?.active);
                const isCreator = Boolean(user.creatorData || (user as any).isCreator);
                const avatarUrl = user.creatorData?.avatar || user.avatar || undefined;

                return (
                  <TableRow key={user.id} className="border-white/10 hover:bg-white/[0.02]">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={avatarUrl ?? undefined} />
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xs">
                            {getInitials(user.email, name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate text-sm font-medium text-white" title={name}>
                            {name}
                          </span>
                          <div className="flex items-center gap-1">
                            {isCreator && (
                              <Badge className="border-0 bg-purple-500/20 px-1.5 py-0 text-[10px] text-purple-300">
                                <UserIcon className="mr-0.5 h-2.5 w-2.5" />
                                {t("creator")}
                              </Badge>
                            )}
                            {(user.creatorData?.username || user.username) && (
                              <span className="truncate text-xs text-white/40">
                                @{user.creatorData?.username || user.username}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-white/80">{user.email}</TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "text-xs",
                          isSubscribed
                            ? "bg-green-500/20 text-green-400 border-green-500/30"
                            : "bg-red-500/20 text-red-400 border-red-500/30",
                        )}
                      >
                        {isSubscribed ? t("active") : t("inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-white/70">
                      {isSubscribed ? formatDate(user.subscription?.expiresAt) : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isVip}
                          onCheckedChange={() =>
                            onVipToggle({
                              id: user.id,
                              vip: !isVip,
                              vipExpiresAt: !isVip
                                ? new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()
                                : (user.vipExpiresAt as string),
                            })
                          }
                          className="data-[state=checked]:bg-yellow-500"
                        />
                        {isVip && <Crown className="h-3.5 w-3.5 text-yellow-500" />}
                      </div>
                    </TableCell>
                    <TableCell>
                      {isVip ? (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 justify-between gap-2 bg-[#1E1E1E] border-[#333333] text-white hover:bg-[#2A2A2A] text-xs"
                            >
                              {user.vipExpiresAt ? formatDate(user.vipExpiresAt) : t("selectDateForVip")}
                              <CalendarIcon className="h-3 w-3" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-2 border-[#333333] bg-[#1E1E1E]" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={user.vipExpiresAt ? new Date(user.vipExpiresAt) : undefined}
                              onSelect={(date) =>
                                onVipToggle({
                                  id: user.id,
                                  vip: user.vip,
                                  vipExpiresAt: date?.toISOString() as string,
                                })
                              }
                              initialFocus
                              className={cn("p-3 pointer-events-auto rounded-md border-none bg-[#1E1E1E] text-white")}
                            />
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <span className="text-sm text-white/40">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-white/70">
                      {formatDate((user as any).createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 bg-[#1E1E1E] border-[#333333] text-white hover:bg-[#2A2A2A]"
                        onClick={() =>
                          navigate(`/users/${user.id}/edit`, {
                            state: {
                              user,
                              from: window.location.pathname + window.location.search,
                            },
                          })
                        }
                      >
                        <PenLine className="h-3.5 w-3.5 mr-1" />
                        {t("edit")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="border-white/10">
                <TableCell colSpan={8} className="text-center text-white/60 py-8">
                  {t("notDefined")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {shouldRenderPagination && pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          pageSizeOptions={pagination.pageSizeOptions ?? [10, 20, 50, 100]}
          showPageSizeOptions={pagination.showPageSizeOptions ?? true}
          showTotalItems={pagination.showTotalItems ?? true}
          setCurrentPage={pagination.onPageChange ?? (() => {})}
          onPageSizeChange={pagination.onPageSizeChange}
          syncWithQueryParams={pagination.syncWithQueryParams ?? false}
        />
      )}
    </div>
  );
};
